import {
  EApiStatus,
  ERROR_TEACHER_BACKEND,
  TeacherStatus,
} from 'constants/common';
import { useFeatureToggle, useSnackbar } from 'hooks/index';
import cookie, { KeyCookie } from 'plugins/cookie/cookie';
import axios from 'plugins/api/axios';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FIRST_ROUTE, PATH } from 'routes/routeConfig';
import { useLoginService } from 'services/LoginService';
import { useLoadingService } from 'services/LoadingService';
import { IProfile, TProfileContext } from 'types/profile';
import { TeacherRecruitmentDto } from 'types/teacherRecruitment';
import { t } from 'utils/i18n';
import { InitTeacherBody } from 'types/teacher';
import { ERemoteConfigParameter } from 'services/FeatureToggleService/remoteConfigService.constants';

const { teachers, teacherRecruit } = axios;

const formatBankName = (bankCode?: string | null, bankName?: string | null) => {
  if (!bankName) return '';
  const [code, name] = bankName.split(' ');
  return bankCode
    ? { bankName }
    : {
        bankCode: code,
        bankName: name,
      };
};

export const ProfileContext = createContext<TProfileContext>({
  profile: {},
  infoTeacherRecruitment: {},
});

export const ProfileProvider: FC = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enabledFeatures] = useFeatureToggle();
  const isTeacherRecruitmentEnabled = enabledFeatures(
    ERemoteConfigParameter.TEACHER_APPLICATION,
  );
  const { enqueueSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoadingService();
  const { isLogin, setIsLogin, logout, username } = useLoginService();
  const [isMount, setIsMount] = useState<boolean>(false);
  const [profile, setProfile] = useState<IProfile>({
    firstName: '',
    lastName: '',
    gender: '',
    status: '',
    createdAt: '',
    updatedAt: '',
    staffType: '',
  });

  const [infoTeacherRecruitment, setInfoTeacherRecruitment] =
    useState<TeacherRecruitmentDto>({});
  const isUnauthorizedPath = () => {
    if (location.pathname === PATH.REGISTER) {
      return true;
    }
    if (location.pathname === PATH.LOGIN) {
      return true;
    }
    if (location.pathname === PATH.VERIFY_EMAIL) {
      return true;
    }
    if (location.pathname === PATH.RESET_PASSWORD) {
      return true;
    }
    if (location.pathname === PATH.RESET_PASSWORD_REQUEST) {
      return true;
    }
    return false;
  };

  const isTeacherRecruitmentPath = () => {
    const pathname = location.pathname.substring(1, location.pathname.length);
    return (
      pathname.length >= FIRST_ROUTE.TEACHER_APPLICATION.length &&
      pathname.substring(0, FIRST_ROUTE.TEACHER_APPLICATION.length) ===
        FIRST_ROUTE.TEACHER_APPLICATION
    );
  };

  const removeToken = () => {
    cookie.remove(KeyCookie.newTeacherClient);
  };

  const handleFetchProfileTeacherRecruitment = async () => {
    showLoading();

    const {
      data: teacherData,
      status: teacherStatus,
      error: teacherErrors,
    } = await teachers.getMe();

    const {
      data: teacherRecruitData,
      status: teacherRecruitStatus,
      error: teacherRecruitError,
    } = await teacherRecruit.get();

    const getTeacherFail = teacherStatus === EApiStatus.FAILURE || !teacherData;
    const getTeacherRecruitFail =
      teacherRecruitStatus === EApiStatus.FAILURE || !teacherRecruitData;

    if (getTeacherFail && getTeacherRecruitFail) {
      logout();
      hideLoading();
      return;
    }

    if (!getTeacherFail) {
      if (!getTeacherRecruitFail) {
        setInfoTeacherRecruitment({
          ...(teacherRecruitData as unknown as TeacherRecruitmentDto),
        });
      }
      hideLoading();
      const profile = teacherData as unknown as IProfile;
      setProfile(() => ({
        ...profile,
        ...formatBankName(profile?.bankCode, profile?.bankName),
      }));
      if (teacherData && teacherData.status === TeacherStatus.UNVERIFIED) {
        if (!isTeacherRecruitmentPath()) {
          navigate(PATH.TEACHER_APPLICATION_CREATE, { replace: true });
        }
      } else {
        isUnauthorizedPath()
          ? navigate(PATH.ROOT, { replace: true })
          : navigate(location.pathname, { replace: true });
      }
      return;
    }

    if (getTeacherRecruitFail) {
      enqueueSnackbar(`${teacherRecruitError?.message}`, {
        variant: 'error',
      });
      removeToken();
      setIsLogin(false);
      hideLoading();
      return;
    }

    if (
      teacherErrors &&
      teacherErrors?.errorCode !== ERROR_TEACHER_BACKEND.TEACHER_IS_NOT_EXIST
    ) {
      enqueueSnackbar(`${teacherErrors?.message}`, {
        variant: 'error',
      });
      removeToken();
      setIsLogin(false);
      hideLoading();
      return;
    }

    // teacherApiFail = true && teacherRecruitApiFail = false
    // add teacherRecruitment data to teacher-backend

    const newAccount = {
      oneClubId: username,
      firstName: teacherRecruitData.firstName,
      lastName: teacherRecruitData.lastName,
      gender: 'male',
      staffType: 'inside',
      status: TeacherStatus.UNVERIFIED,
      email: teacherRecruitData.email,
    };

    const {
      data: initTeacherData,
      error: initError,
      status: initStatus,
    } = await teachers.initTeacher(newAccount as InitTeacherBody);

    if (initStatus === EApiStatus.FAILURE) {
      enqueueSnackbar(`${initError?.errorCode}_${initError?.message}`, {
        variant: 'error',
      });
      removeToken();
      setIsLogin(false);
      hideLoading();
      return;
    }

    const { data } = await teachers.getMe();
    if (data && data.status === TeacherStatus.UNVERIFIED) {
      hideLoading();
      const latestData = data as unknown as IProfile;
      setProfile(() => ({
        ...latestData,
        ...formatBankName(latestData?.bankCode, latestData?.bankName),
      }));
      if (!isTeacherRecruitmentPath()) {
        navigate(PATH.TEACHER_APPLICATION_CREATE, { replace: true });
      }
      return;
    }

    const initData = initTeacherData as unknown as IProfile;
    setProfile(() => ({
      ...initData,
      ...formatBankName(initData?.bankCode, initData.bankName),
    }));
    hideLoading();
    if (initData.status === TeacherStatus.UNVERIFIED) {
      if (!isTeacherRecruitmentPath()) {
        navigate(PATH.TEACHER_APPLICATION_CREATE, { replace: true });
      }
    } else {
      location.pathname === PATH.LOGIN
        ? navigate(PATH.ROOT, { replace: true })
        : navigate(location.pathname, { replace: true });
    }
  };

  const handleFetchProfileOfficialTeacher = async () => {
    showLoading();
    const { data, error, status } = await teachers.getMe();
    if (status !== EApiStatus.SUCCESS) {
      enqueueSnackbar(`${error?.errorCode}_${error?.message}`, {
        variant: 'error',
      });
      await logout();
      hideLoading();
    } else if (status === EApiStatus.SUCCESS && !data) {
      enqueueSnackbar('查無教師資料', { variant: 'error' });
      removeToken();
      setIsLogin(false);
      hideLoading();
    } else if (status === EApiStatus.SUCCESS) {
      if (username) {
        const { data: teacherRecruitData, status: teacherRecruitStatus } =
          await teacherRecruit.get();
        const getTeacherRecruitFail =
          teacherRecruitStatus === EApiStatus.FAILURE || !teacherRecruitData;

        if (!getTeacherRecruitFail) {
          setInfoTeacherRecruitment({
            ...(teacherRecruitData as unknown as TeacherRecruitmentDto),
          });
        }
      }

      const profile = data as unknown as IProfile;
      setProfile(() => ({
        ...profile,
        ...formatBankName(profile?.bankCode, profile?.bankName),
      }));
      hideLoading();
      if (profile.status === TeacherStatus.UNVERIFIED) {
        if (!isTeacherRecruitmentPath()) {
          navigate(PATH.TEACHER_APPLICATION_CREATE, { replace: true });
        }
      } else {
        isUnauthorizedPath()
          ? navigate(PATH.ROOT, { replace: true })
          : navigate(location.pathname, { replace: true });
      }
    }
  };

  const fetchProfile = async () => {
    if (isTeacherRecruitmentEnabled) {
      handleFetchProfileTeacherRecruitment();
      return;
    }
    handleFetchProfileOfficialTeacher();
  };

  const updateProfile = async (payload: IProfile) => {
    showLoading();
    // PATCH 回傳的是更新前的資料，直接用送出的 payload 更新 profile
    const { status } = await teachers.updateMe(payload);
    if (status === EApiStatus.SUCCESS) {
      setProfile(prev => ({ ...prev, ...payload }));
      enqueueSnackbar(
        t(
          'services.profileService.updateTeacherProfileSuccess',
          '更新教師資料成功',
        ),
      );
    } else if (status === EApiStatus.FAILURE) {
      enqueueSnackbar(
        t(
          'services.profileService.updateTeacherProfileFailed',
          '更新教師資料失敗',
        ),
        { variant: 'error' },
      );
      // 產生新 reference，讓各表單的 useEffect 把 formData 重設回原本的 profile
      setProfile(prev => ({ ...prev }));
    }
    hideLoading();
  };
  const updateInfoTeacherRecruitment = async (
    payload: TeacherRecruitmentDto,
  ) => {
    setInfoTeacherRecruitment(payload);
  };

  useEffect(() => {
    setIsMount(true);
  }, []);

  useEffect(() => {
    if (isMount) {
      if (isLogin) {
        fetchProfile();
      } else {
        hideLoading();
        if (!isUnauthorizedPath()) {
          navigate(PATH.LOGIN, { replace: true });
        }
      }
    }
  }, [isMount, isLogin]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        updateProfile,
        infoTeacherRecruitment,
        updateInfoTeacherRecruitment,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileService = () =>
  useContext<TProfileContext>(ProfileContext);
